import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { TrendingUp, Sparkles, GitCompareArrows, ArrowRight, Users2, PieChart } from 'lucide-react';
import { useFilters } from '@/context/FilterContext';
import { useTenant } from '@/context/TenantContext';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { usePost } from '@/lib/useData';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { ScoreGauge } from '@/components/common/ScoreGauge';
import { ComparisonBars } from '@/components/charts/ComparisonBars';
import { AgeDonut } from '@/components/charts/AgeDonut';
import { SegmentChips } from '@/components/common/SegmentChips';
import { AgeOverlayView } from '@/components/kpi/SegmentCharts';
import { useSegmentQueries } from '@/lib/useSegment';
import { EmptyState } from '@/components/common/EmptyState';
import { TrendBadge } from '@/components/common/TrendBadge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PERIOD_PRESETS, computePeriod } from '@/lib/periods';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

const METRIC_LABELS = {
  bs_rue: 'BS signés', bs_reel: 'BS validés', taux_transfo: 'Taux transfo',
  don_moyen: 'Don moyen', montant: 'Montant', heures: 'Heures', productivite: 'Productivité', age_moyen: 'Âge moyen',
};
const fmtMetric = (k, v) => k === 'taux_transfo' ? fmtPct(v) : (k === 'don_moyen' || k === 'montant') ? fmtEur(v, k === 'montant' ? 0 : 2) : (k === 'productivite' || k === 'age_moyen') ? fmtNum(v, 2) : fmtInt(v);

export default function Analyses() {
  const [params] = useSearchParams();
  const { body, options, parrainageOn, parrainageSeuil } = useFilters();
  const { association } = useTenant();
  const { isAdmin } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [pa, setPa] = useState('last_month');
  const [pb, setPb] = useState('this_month');
  const [dim, setDim] = useState('campaign_code');
  const [cmp, setCmp] = useState(null);
  const [loadingCmp, setLoadingCmp] = useState(false);

  // Age pyramid (monthly / yearly)
  const [ageGran, setAgeGran] = useState('month');
  const [agePeriod, setAgePeriod] = useState('overall');
  const ageBody = useMemo(() => (body?.association ? { ...body, granularity: ageGran } : null), [body, ageGran]);
  const agePyr = usePost('/analytics/age-pyramid', ageBody);
  const agePeriods = agePyr.data?.periods || [];
  const ageOverall = agePyr.data?.overall;
  const ageSel = agePeriod === 'overall'
    ? ageOverall
    : (agePeriods.find((p) => p.period === agePeriod) || ageOverall);

  // Superposition par famille de don (parrainage) — réutilise le param `segment`.
  const [ageSegs, setAgeSegs] = useState(['ensemble']);
  const ageSeg = useSegmentQueries('/analytics/age-pyramid', body, { granularity: ageGran },
    parrainageOn ? ageSegs : [], { parrainageOn, seuil: parrainageSeuil });
  const pickPeriod = (d) => (!d ? null : (agePeriod === 'overall' ? d.overall : (d.periods || []).find((p) => p.period === agePeriod) || d.overall));
  const pyramidOveralls = {
    ensemble: pickPeriod(ageSeg.byKey.ensemble?.data),
    regulier: pickPeriod(ageSeg.byKey.regulier?.data),
    parrainage: pickPeriod(ageSeg.byKey.parrainage?.data),
  };

  const genSummary = async () => {
    setLoadingSummary(true);
    try {
      const { data } = await api.post('/executive-summary', body);
      setSummary(data);
    } catch (e) { toast.error('Génération du résumé impossible'); }
    finally { setLoadingSummary(false); }
  };

  useEffect(() => {
    if (isAdmin && params.get('summary') === '1' && body?.association && !summary) genSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, body?.association]);

  const runCompare = async () => {
    setLoadingCmp(true);
    try {
      const A = computePeriod(pa, undefined, options?.date_min, options?.date_max);
      const B = computePeriod(pb, undefined, options?.date_min, options?.date_max);
      const { data } = await api.post('/analytics/compare', {
        association: body.association, dim, granularity: 'week',
        periodA: A, periodB: B,
      });
      setCmp(data);
    } catch (e) { toast.error('Comparaison impossible'); }
    finally { setLoadingCmp(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Analyses" icon={TrendingUp} subtitle={isAdmin ? 'Synthèse par IA et comparaison décisionnelle' : 'Comparaison décisionnelle et pyramide des âges'} />

      {/* Executive summary — SHARITI internal only (AI), hidden for associations */}
      {isAdmin && (
      <SectionCard title="Résumé Direction" subtitle="Synthèse automatique générée par IA"
        action={<Button size="sm" className="gap-1.5 rounded-xl text-white" style={{ background: 'var(--tenant-accent-500)' }}
          onClick={genSummary} disabled={loadingSummary} data-testid="generate-summary-button">
          <Sparkles className="h-4 w-4" /> {loadingSummary ? 'Génération…' : 'Générer'}
        </Button>}>
        {loadingSummary ? (
          <div className="space-y-2"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /></div>
        ) : summary ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <p className="text-sm leading-7 text-foreground" data-testid="summary-narrative">{summary.narrative}</p>
            </div>
            <div className="flex flex-col items-center justify-center rounded-2xl bg-muted/40 p-4">
              <ScoreGauge score={summary.score?.score} rating={summary.score?.rating} size={170} />
            </div>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-muted-foreground">Cliquez sur « Générer » pour obtenir une synthèse directionnelle de la période.</p>
        )}
      </SectionCard>
      )}

      {/* Age pyramid — monthly / yearly */}
      <SectionCard
        title="Pyramide des âges"
        subtitle="Répartition et âge moyen des donateurs, par mois ou par année"
        action={
          <div className="flex items-center gap-2">
            <Select value={ageGran} onValueChange={(v) => { setAgeGran(v); setAgePeriod('overall'); }}>
              <SelectTrigger className="h-9 w-[120px] rounded-xl bg-card" data-testid="age-gran-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Par mois</SelectItem>
                <SelectItem value="year">Par année</SelectItem>
              </SelectContent>
            </Select>
            <Select value={agePeriod} onValueChange={setAgePeriod}>
              <SelectTrigger className="h-9 w-[170px] rounded-xl bg-card" data-testid="age-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="overall">Ensemble</SelectItem>
                {agePeriods.map((p) => <SelectItem key={p.period} value={p.period}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
        testid="age-pyramid-card">
        {parrainageOn && (
          <SegmentChips value={ageSegs} onChange={setAgeSegs} testid="pyramid-segment-chips" className="mb-4" />
        )}
        {(parrainageOn ? ageSeg.isLoading : agePyr.isLoading) ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Skeleton className="h-[240px] lg:col-span-2" /><Skeleton className="h-[240px]" />
          </div>
        ) : parrainageOn ? (
          <AgeOverlayView segs={ageSegs} overalls={pyramidOveralls}
            emptyBody="Aucune donnée d'âge pour cette sélection." />
        ) : ageSel && ageSel.total > 0 ? (
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2" data-testid="age-pyramid-donut">
              <AgeDonut data={ageSel.age_distribution} />
            </div>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
              <div className="rounded-2xl border bg-card p-4">
                <div className="flex items-center gap-1.5 text-muted-foreground"><Users2 className="h-4 w-4" /><span className="text-xs">Âge moyen</span></div>
                <p className="mt-1 font-display text-3xl font-semibold tabular" data-testid="age-pyramid-moyen">{fmtNum(ageSel.age_moyen, 1)} ans</p>
                <p className="text-xs text-muted-foreground">{agePeriod === 'overall' ? 'Sur l’ensemble des données' : ageSel.label}</p>
              </div>
              <div className="rounded-2xl border bg-card p-4">
                <div className="flex items-center gap-1.5 text-muted-foreground"><PieChart className="h-4 w-4" /><span className="text-xs">Donateurs profilés</span></div>
                <p className="mt-1 font-display text-3xl font-semibold tabular" data-testid="age-pyramid-total">{fmtInt(ageSel.total)}</p>
                <p className="text-xs text-muted-foreground">BS validés avec âge renseigné</p>
              </div>
            </div>
          </div>
        ) : (
          <EmptyState body="Aucune donnée d'âge disponible pour cette sélection." />
        )}
      </SectionCard>

      {/* Comparator */}
      <SectionCard title="Comparateur de périodes" subtitle="Mesurez la progression entre deux périodes"
        action={<GitCompareArrows className="h-5 w-5 text-muted-foreground" />}>
        <div className="flex flex-wrap items-end gap-3">
          <PeriodSelect label="Période A" value={pa} onChange={setPa} testid="compare-period-a" />
          <ArrowRight className="mb-2 h-4 w-4 text-muted-foreground" />
          <PeriodSelect label="Période B" value={pb} onChange={setPb} testid="compare-period-b" />
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Dimension</span>
            <Select value={dim} onValueChange={setDim}>
              <SelectTrigger className="h-9 w-[170px] rounded-xl bg-card" data-testid="compare-dim"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="campaign_code">Code campagne</SelectItem>
                <SelectItem value="region">Région</SelectItem>
                <SelectItem value="canal">Canal</SelectItem>
                <SelectItem value="responsable">Responsable</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button className="h-9 rounded-xl text-white" style={{ background: 'var(--tenant-accent-500)' }} onClick={runCompare} disabled={loadingCmp} data-testid="run-compare-button">
            {loadingCmp ? 'Calcul…' : 'Comparer'}
          </Button>
        </div>

        {cmp && (
          <div className="mt-6 space-y-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {Object.entries(cmp.deltas).map(([k, d]) => (
                <div key={k} className="rounded-xl border bg-card p-3">
                  <p className="text-[11px] text-muted-foreground">{METRIC_LABELS[k]}</p>
                  <p className="mt-1 font-display text-lg font-semibold tabular">{fmtMetric(k, d.b)}</p>
                  <div className="mt-1"><TrendBadge value={d.pct} invert={k === 'age_moyen'} /></div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border bg-card p-4">
              <p className="mb-3 text-sm font-semibold">Évolution hebdomadaire (BS validés)</p>
              <ComparisonBars
                data={mergeSeries(cmp.series_a, cmp.series_b)}
                xTickFormatter={(v) => v}
                formatTip={(v) => fmtInt(v)}
                series={[{ key: 'a', name: 'Période A', color: '#C7CDD6' }, { key: 'b', name: 'Période B', color: 'var(--tenant-accent-500)' }]} />
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

const PeriodSelect = ({ label, value, onChange, testid }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-[160px] rounded-xl bg-card" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {PERIOD_PRESETS.filter((p) => p.key !== 'all').map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

function mergeSeries(a = [], b = []) {
  const map = {};
  a.forEach((p) => { map[p.period] = { period: p.period, a: p.bs_reel, b: 0 }; });
  b.forEach((p) => { map[p.period] = { ...(map[p.period] || { period: p.period, a: 0 }), b: p.bs_reel }; });
  return Object.values(map).sort((x, y) => x.period.localeCompare(y.period));
}
